import { AuthProvider } from '@/components/AuthProvider';
import { KYCGuard } from '@/components/KYCGuard';
import { PrimeAccessGuard } from '@/components/PrimeAccessGuard';
import { TokenApprovalGuard } from '@/components/TokenApprovalGuard';
import { WalletConnectionGuard } from '@/components/WalletConnectionGuard';
import { FirebaseAuthProvider } from '@/contexts/FirebaseAuthContext';
import { LoadingProvider } from '@/contexts/LoadingContext';
import { useLocation } from 'react-router';
import { Toaster } from 'react-hot-toast';
import Web3Providers from '@/components/Web3Providers';
import MobileLayout from '@/components/MobileLayout';
import DesktopLayout from '@/components/DesktopLayout';
import { useDeviceType } from '@/hooks/useDeviceType';

const ConditionalGuards = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const isPublicPath = pathname === '/login';

  if (isPublicPath) {
    return <>{children}</>;
  }

  return (
    <KYCGuard>
      <PrimeAccessGuard>
        <TokenApprovalGuard>{children}</TokenApprovalGuard>
      </PrimeAccessGuard>
    </KYCGuard>
  );
};

const LayoutSwitcher = ({ children }: { children: React.ReactNode }) => {
  const deviceType = useDeviceType();
  const { pathname } = useLocation();
  if (pathname === '/login') return <>{children}</>;
  const Layout = deviceType === 'MOBILE' ? MobileLayout : DesktopLayout;

  return <Layout>{children}</Layout>;
};

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <FirebaseAuthProvider>
      <LoadingProvider>
        <Web3Providers>
          <AuthProvider>
            <WalletConnectionGuard>
              <ConditionalGuards>
                <LayoutSwitcher>{children}</LayoutSwitcher>
              </ConditionalGuards>
            </WalletConnectionGuard>
          </AuthProvider>
        </Web3Providers>
        <Toaster
          position='top-center'
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </LoadingProvider>
    </FirebaseAuthProvider>
  );
}
